import type { MaybeRefOrGetter } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';

import { useQuery, useQueryCache } from '@pinia/colada';
import { computed, toValue } from 'vue';

import type {
  CommunitySettingsCategoriesParams,
  CommunitySettingsTagsParams,
} from '@/apis/community-settings.types';

import {
  getCommunitySettingsCategories,
  getCommunitySettingsCategoryPage,
  getCommunitySettingsTagCatalog,
  getCommunitySettingsTaggingTasks,
  getCommunitySettingsTags,
} from '@/apis/community-settings';
import { MUTABLE_QUERY_OPTIONS, QueryKeys } from '@/shared/queries';

function sortedIds(ids: number[]) {
  return [...new Set(ids)].toSorted((a, b) => a - b);
}

export function invalidateCommunitySettingsGuard(to: RouteLocationNormalized) {
  const queryCache = useQueryCache();
  const showCategories = to.query.tab === 'tag-categories';
  const showTags = !showCategories && to.query.tab !== 'sentiment-and-spam';
  void Promise.allSettled(
    [
      { key: QueryKeys.community.settingsTagsRoot(), refetch: showTags },
      { key: QueryKeys.community.settingsCategoriesRoot(), refetch: showTags || showCategories },
      { key: QueryKeys.community.settingsTaggingTasksRoot(), refetch: showTags },
      { key: QueryKeys.community.settingsTagCatalogRoot(), refetch: showCategories },
    ].map(({ key, refetch }) => queryCache.invalidateQueries({ key }, refetch)),
  );
}

export function useCommunitySettingsTags(params: MaybeRefOrGetter<CommunitySettingsTagsParams>) {
  const request = computed<CommunitySettingsTagsParams>(() => {
    const value = toValue(params);
    return {
      ...value,
      brandIds: sortedIds(value.brandIds),
      categoryIds: value.categoryIds?.length
        ? [...new Set(value.categoryIds)].toSorted((a, b) => String(a).localeCompare(String(b)))
        : undefined,
    };
  });
  return useQuery({
    ...MUTABLE_QUERY_OPTIONS,
    key: () => QueryKeys.community.settingsTags(request.value),
    enabled: () => request.value.brandIds.length > 0,
    query: async ({ signal }) => (await getCommunitySettingsTags(request.value, { signal })).data,
  });
}

export function useCommunitySettingsCategories(brandIds: MaybeRefOrGetter<number[]>) {
  const ids = computed(() => sortedIds(toValue(brandIds)));
  const { data, ...query } = useQuery({
    ...MUTABLE_QUERY_OPTIONS,
    key: () => QueryKeys.community.settingsCategories(ids.value),
    enabled: () => ids.value.length > 0,
    query: ({ signal }) => getCommunitySettingsCategories(ids.value, { signal }),
  });
  return { categories: computed(() => data.value ?? []), ...query };
}

export function useCommunitySettingsTaggingTasks(brandIds: MaybeRefOrGetter<number[]>) {
  const ids = computed(() => sortedIds(toValue(brandIds)));
  const { data, ...query } = useQuery({
    ...MUTABLE_QUERY_OPTIONS,
    key: () => QueryKeys.community.settingsTaggingTasks(ids.value),
    enabled: () => ids.value.length > 0,
    query: ({ signal }) => getCommunitySettingsTaggingTasks(ids.value, { signal }),
  });
  return { tasks: computed(() => data.value ?? []), ...query };
}

export function useCommunitySettingsCategoryTable(
  params: MaybeRefOrGetter<CommunitySettingsCategoriesParams>,
) {
  const request = computed<CommunitySettingsCategoriesParams>(() => {
    const value = toValue(params);
    return {
      ...value,
      brandIds: sortedIds(value.brandIds),
      categoryIds: value.categoryIds?.length ? sortedIds(value.categoryIds) : undefined,
      tagIds: value.tagIds?.length ? sortedIds(value.tagIds) : undefined,
    };
  });
  return useQuery({
    ...MUTABLE_QUERY_OPTIONS,
    key: () => QueryKeys.community.settingsCategoryTable(request.value),
    enabled: () => request.value.brandIds.length > 0,
    query: async ({ signal }) =>
      (await getCommunitySettingsCategoryPage(request.value, { signal })).data,
  });
}

export function useCommunitySettingsTagCatalog(brandIds: MaybeRefOrGetter<number[]>) {
  const ids = computed(() => sortedIds(toValue(brandIds)));
  const { data, ...query } = useQuery({
    ...MUTABLE_QUERY_OPTIONS,
    key: () => QueryKeys.community.settingsTagCatalog(ids.value),
    enabled: () => ids.value.length > 0,
    query: ({ signal }) => getCommunitySettingsTagCatalog(ids.value, { signal }),
  });
  return { tags: computed(() => data.value ?? []), ...query };
}
